import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";
import { buildBikeMesh, clampLean, PALETTE_CYAN } from "../_lib/moto";

/**
 * Slipstream — first-person endless corridor; lean to dodge obstacles
 * and graze them for multiplier-window scoring.
 * Game 18 of platform games + 6-game rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/18-slipstream/
 * Source IP: Tron-light-cycle / Star Fox tunnel canon.
 *
 * MVP scope:
 *   - Deterministic obstacle schedule from seed
 *   - First-person camera mounted above-behind the bike
 *   - Recycled corridor strips + obstacle pool
 *   - Auto-throttle; player leans left/right to dodge
 *   - Near-miss detection within NEAR_MISS_RADIUS (lateral)
 *   - Multiplier window: each near-miss bumps multiplier (cap 5),
 *     decays back to 1× after MULTIPLIER_WINDOW_MS of no event
 *   - Score = ∫ forward_speed × multiplier dt
 *   - 120s run cap; collision ends run early
 *
 * Deferred to v2 polish (NOT in MVP):
 *   - Biome cycling
 *   - Per-kind obstacle AABBs + visuals
 *   - Engine RPM audio modulation
 *   - HUDChrome shared helper
 *   - Post-processing (bloom, FXAA)
 */

// ============================================================
// Constants — must stay byte-identical with scoring-slipstream.ts
// (parity contract per dossier §02 §4.2)
// ============================================================

export const RUN_MS = 120_000;
export const SAMPLE_INTERVAL_MS = 33;
export const MAX_SAMPLES = 3600;
export const MAX_NEAR_MISSES = 240;

export const MAX_FORWARD_SPEED = 60.0;
export const MAX_LATERAL_SPEED = 16.0;
export const CORRIDOR_HALF_WIDTH = 4.0;

export const NEAR_MISS_RADIUS = 0.5;
export const MULTIPLIER_WINDOW_MS = 3000;
export const MAX_MULTIPLIER = 5;
export const BIOME_ROTATION_M = 1500;

export const BIKE_HALF_X = 0.45;
export const BIKE_HALF_Z = 1.6;

export const OBSTACLE_HALF_X = 1.0;
export const OBSTACLE_HALF_Z = 2.5;

export const CORRIDOR_LENGTH = 7200;
export const OBSTACLE_MIN_GAP_Z = 18;
export const OBSTACLE_MAX_GAP_Z = 42;
export const OBSTACLE_END_GAP_SCALE = 0.55;
export const POWERUP_INTERVAL_Z = 420;
export const POWERUP_FIRST_Z = 200;
export const POWERUP_PICKUP_HALF_X = 0.8;
export const POWERUP_BONUS = 800;
export const POWERUP_EXTEND_MS = 2000;

// Per-frame physics — derived constants (client-only).
const FORWARD_ACCEL = 30.0;      // auto-throttle ramp toward MAX_FORWARD_SPEED
const LATERAL_ACCEL = 28.0;      // lean → lateral velocity
const LATERAL_DAMP = 8.0;        // toward-zero pull when no lean input
const CAMERA_HEIGHT = 1.6;
const CAMERA_BEHIND_Z = 3.2;     // how far behind the bike the camera sits

/** v5: per-biome palette. Biome index = floor(z / BIOME_ROTATION_M) % BIOMES.
 *  Visuals lerp between biome palettes as z advances; no scoring impact. */
interface BiomePalette {
  bg: number;
  fog: number;
  floor: number;
  wall: number;
  wallEmissive: number;
}
const BIOMES: BiomePalette[] = [
  // 0 — Helios (warm dawn)
  { bg: 0x040a18, fog: 0x040a18, floor: 0x0a1428, wall: 0x1c2848, wallEmissive: 0x2a78c8 },
  // 1 — Vermilion (rust + ember)
  { bg: 0x180802, fog: 0x180802, floor: 0x281208, wall: 0x481a20, wallEmissive: 0xc85428 },
  // 2 — Abyss (deep purple)
  { bg: 0x0a0418, fog: 0x0a0418, floor: 0x180828, wall: 0x281848, wallEmissive: 0x9a72ff },
  // 3 — Cyan (cool dusk)
  { bg: 0x041818, fog: 0x041818, floor: 0x082828, wall: 0x184848, wallEmissive: 0x46ffe0 },
];

// ============================================================
// Obstacle schedule — byte-identical with scoring-slipstream
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Obstacle {
  idx: number;
  z: number;
  x: number;
}

export interface Powerup {
  idx: number;
  z: number;
  x: number;
}

export function generatePowerups(seed: number): Powerup[] {
  const rng = makeRng(seed ^ 0x504f5752);
  const out: Powerup[] = [];
  const xRange = CORRIDOR_HALF_WIDTH - POWERUP_PICKUP_HALF_X;
  let z = POWERUP_FIRST_Z;
  let idx = 0;
  while (z < CORRIDOR_LENGTH) {
    const x = (rng() * 2 - 1) * xRange;
    out.push({ idx, z, x });
    idx += 1;
    z += POWERUP_INTERVAL_Z;
  }
  return out;
}

export function generateObstacles(seed: number): Obstacle[] {
  const rng = makeRng(seed ^ 0x534c4950);
  const out: Obstacle[] = [];
  let z = 60;
  let idx = 0;
  const xRange = CORRIDOR_HALF_WIDTH - OBSTACLE_HALF_X;
  while (z < CORRIDOR_LENGTH) {
    // v2(b) escalation: linearly shrink the gap window as z grows.
    const t = Math.min(1, (z - 60) / Math.max(1, CORRIDOR_LENGTH - 60));
    const gapScale = 1 + (OBSTACLE_END_GAP_SCALE - 1) * t;
    const minGap = OBSTACLE_MIN_GAP_Z * gapScale;
    const maxGap = OBSTACLE_MAX_GAP_Z * gapScale;
    const gap = minGap + rng() * (maxGap - minGap);
    z += gap;
    if (z >= CORRIDOR_LENGTH) break;
    const x = (rng() * 2 - 1) * xRange;
    out.push({ idx, z, x });
    idx += 1;
  }
  return out;
}

// ============================================================
// Types (wire shapes match scoring-slipstream.ts)
// ============================================================

export interface PositionSample {
  t: number;
  x: number;
  z: number;
  lean: number;
}

export interface NearMissEvent {
  t: number;
  obstacle_idx: number;
  lateral_distance: number;
}

export interface HitEvent {
  t: number;
  obstacle_idx: number;
}

export interface SlipstreamResult {
  distance_m: number;
  near_misses: number;
  peak_multiplier: number;
  powerups_collected: number;
  hit: boolean;
  duration_ms: number;
  samples: PositionSample[];
  nearMisses: NearMissEvent[];
  hitEvent: HitEvent | null;
  scoreEstimate: number;
  /** v5: count of biomes the player rode through during the run. */
  biomes_visited: number;
}

export interface SlipstreamCallbacks {
  onHud: (runMs: number, distance: number, multiplier: number, scoreEstimate: number) => void;
  onGameOver: (result: SlipstreamResult) => void;
}

// ============================================================
// Game class
// ============================================================

const q01 = (v: number) => Math.round(v * 100) / 100;
const q1 = (v: number) => Math.round(v * 10) / 10;

export class Slipstream {
  private engine: Engine;
  private audio = new AudioBed();
  private obstacles: Obstacle[];
  private obstacleMeshes: THREE.Mesh[] = [];
  // Phase 2: power-ups
  private powerups: Powerup[] = [];
  private powerupMeshes: THREE.Mesh[] = [];
  private collectedPowerupIdxs = new Set<number>();
  private nextPowerupIdx = 0;
  private powerupsCollected = 0;
  private bikeGroup: THREE.Group;
  private cameraRig: THREE.Object3D;

  // Bike state
  private bx = 0;             // lateral
  private bz = 0;             // forward
  private bvx = 0;            // lateral velocity
  private bvz = 0;            // forward velocity
  private leanInput = 0;      // -1..1, smoothed
  private leanRaw = 0;        // -1..1, from keys

  // Schedule cursor
  private nextObstacleIdx = 0;

  // Recordings
  private startedAt = 0;
  private samples: PositionSample[] = [];
  private nearMisses: NearMissEvent[] = [];
  private hitEvent: HitEvent | null = null;
  private sampleTimer = 0;

  // v6 — parallax starfield layers parented to the camera rig so the
  // stars travel with the bike, reading as distant depth at the
  // tunnel-mouth vanishing point. Per memory
  // feedback-sibling-games-shallow: the rebrand pack needed real depth
  // cues, not just a 3D engine running 2D primitives.
  private starLayers: THREE.Points[] = [];

  // Multiplier state (client-side estimate for HUD only;
  // server re-derives authoritatively).
  private multiplier = 1;
  private multiplierExpiry = -1;
  private peakMultiplier = 1;
  private scoreEstimate = 0;

  private biomesVisited = new Set<number>();
  private gameOver = false;
  private cb: SlipstreamCallbacks;

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.leanRaw = -1;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.leanRaw = 1;
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") { if (this.leanRaw === -1) this.leanRaw = 0; }
    if (e.code === "ArrowRight" || e.code === "KeyD") { if (this.leanRaw === 1) this.leanRaw = 0; }
  };

  // Mobile touch: relative-to-initial-touch X drag → continuous lean.
  // Origin captured on pointerdown; subsequent moves set leanRaw to a
  // continuous [-1, 1] based on (clientX - origin) / TOUCH_RADIUS_PX.
  // The full screen acts as the joystick pad — no visible knob. Touch
  // end zeros lean; keyboard arrows still work in parallel for desktop.
  private static readonly TOUCH_RADIUS_PX = 90;  // px of drag = full lean
  private touchEl: HTMLElement | null = null;
  private touchId: number | null = null;
  private touchOriginX = 0;
  private onTouchDown = (e: PointerEvent) => {
    e.preventDefault();
    this.touchId = e.pointerId;
    this.touchOriginX = e.clientX;
    if (this.touchEl) this.touchEl.setPointerCapture(e.pointerId);
    // Don't set lean on initial press — origin reset only.
    this.leanRaw = 0;
  };
  private onTouchMove = (e: PointerEvent) => {
    if (this.touchId !== e.pointerId) return;
    e.preventDefault();
    const dx = e.clientX - this.touchOriginX;
    const norm = dx / Slipstream.TOUCH_RADIUS_PX;
    this.leanRaw = Math.max(-1, Math.min(1, norm));
  };
  private onTouchUp = (e: PointerEvent) => {
    if (this.touchId !== e.pointerId) return;
    if (this.touchEl) {
      try { this.touchEl.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
    }
    this.touchId = null;
    this.leanRaw = 0;
  };

  constructor(canvas: HTMLCanvasElement, cb: SlipstreamCallbacks, seed: number) {
    this.cb = cb;
    this.obstacles = generateObstacles(seed);
    this.bikeGroup = buildBikeMesh(PALETTE_CYAN);

    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    this.engine.scene.background = new THREE.Color(0x040a18);
    this.engine.scene.fog = new THREE.Fog(0x040a18, 30, 140);

    // Lighting: ambient + a forward-mounted point light (bike headlight feel)
    this.engine.scene.add(new THREE.AmbientLight(0x405070, 0.6));
    const headlight = new THREE.PointLight(0x66e0ff, 1.4, 60, 1.4);
    headlight.position.set(0, 1.6, 5);
    this.engine.scene.add(headlight);
    const sun = new THREE.DirectionalLight(0xfff0e0, 0.6);
    sun.position.set(2, 14, -4);
    this.engine.scene.add(sun);

    // Camera rig: parented forward of the bike's nose so the player
    // sees the corridor ahead with the handlebars visible at the
    // bottom of the frame.
    this.cameraRig = new THREE.Object3D();
    this.engine.scene.add(this.cameraRig);
    this.cameraRig.add(this.bikeGroup);
    this.bikeGroup.position.set(0, 0, 0);

    // v6 — three parallax starfield layers parented to the camera rig
    // (so the stars travel with the bike) at varying forward Z so
    // they read as different depths at the tunnel-mouth vanishing
    // point. Fog ends at z=140; stars sit beyond fog as faint motes.
    // Per memory feedback-sibling-games-shallow.
    const makeStars = (count: number, z: number, spread: number, opts: { size: number; color: number }) => {
      const positions = new Float32Array(count * 3);
      let s = (seed ^ Math.imul(z | 0, 0x9e3779b1)) | 0;
      const r = () => {
        s = (s + 0x6d2b79f5) | 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
      for (let i = 0; i < count; i++) {
        positions[i * 3] = (r() - 0.5) * spread * 2;
        positions[i * 3 + 1] = (r() - 0.5) * spread * 0.6 + 4;  // bias upward (sky)
        positions[i * 3 + 2] = z + (r() - 0.5) * 30;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      const mat = new THREE.PointsMaterial({
        color: opts.color, size: opts.size, sizeAttenuation: true,
        transparent: true, opacity: 0.85, depthWrite: false, fog: false,
      });
      const pts = new THREE.Points(geo, mat);
      this.cameraRig.add(pts);
      this.starLayers.push(pts);
    };
    makeStars(500, 380, 180, { size: 0.9, color: 0x8a92b8 });   // far — dim cool stars
    makeStars(220, 240, 100, { size: 0.75, color: 0xb0b8d8 });  // mid
    makeStars(90,  170,  60, { size: 0.65, color: 0xffe0c0 });  // near (just past fog edge) — warm

    // Mount the camera on the rig, slightly above and behind.
    this.engine.camera.position.set(0, CAMERA_HEIGHT, -CAMERA_BEHIND_Z);
    this.engine.camera.lookAt(0, 1.0, 8);
    this.engine.camera.fov = 75;
    this.engine.camera.near = 0.1;
    this.engine.camera.far = 220;
    this.engine.camera.updateProjectionMatrix();
    this.cameraRig.add(this.engine.camera);

    // Corridor: two side walls + floor strip, recycled by translating
    // segments past the bike back to ahead-of-bike.
    this.buildCorridor();
    this.buildObstacleMeshes();
    this.powerups = generatePowerups(seed);
    this.buildPowerupMeshes();

    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.mountTouch();
  }

  private mountTouch() {
    this.touchEl = document.getElementById("slipstream-touch");
    if (this.touchEl) {
      this.touchEl.addEventListener("pointerdown", this.onTouchDown);
      this.touchEl.addEventListener("pointermove", this.onTouchMove);
      this.touchEl.addEventListener("pointerup", this.onTouchUp);
      this.touchEl.addEventListener("pointercancel", this.onTouchUp);
    }
  }
  private unmountTouch() {
    if (this.touchEl) {
      this.touchEl.removeEventListener("pointerdown", this.onTouchDown);
      this.touchEl.removeEventListener("pointermove", this.onTouchMove);
      this.touchEl.removeEventListener("pointerup", this.onTouchUp);
      this.touchEl.removeEventListener("pointercancel", this.onTouchUp);
    }
  }

  private corridorSegments: THREE.Group[] = [];
  private readonly CORRIDOR_SEGMENT_LENGTH = 30;
  private readonly CORRIDOR_SEGMENT_COUNT = 8;
  // v5: biome cycling — shared materials, lerped per frame.
  private floorMat!: THREE.MeshStandardMaterial;
  private wallMat!: THREE.MeshStandardMaterial;
  private currentBiomeIdx = 0;

  private buildCorridor() {
    // Build N segments and tile them along z. Each segment carries
    // its own floor strip + 2 side walls. As the bike advances, the
    // earliest segment is moved CORRIDOR_SEGMENT_COUNT segments forward.
    // v5: shared biome-cycled materials (single instance per material;
    // updates to .color/.emissive propagate to all segments at once).
    this.floorMat = new THREE.MeshStandardMaterial({
      color: BIOMES[0].floor, roughness: 0.85, metalness: 0.15,
    });
    this.wallMat = new THREE.MeshStandardMaterial({
      color: BIOMES[0].wall, emissive: BIOMES[0].wallEmissive,
      emissiveIntensity: 0.6, roughness: 0.4, metalness: 0.3,
      side: THREE.DoubleSide,
    });
    for (let i = 0; i < this.CORRIDOR_SEGMENT_COUNT; i++) {
      const g = new THREE.Group();
      const segL = this.CORRIDOR_SEGMENT_LENGTH;
      const floorGeo = new THREE.PlaneGeometry(CORRIDOR_HALF_WIDTH * 2, segL);
      floorGeo.rotateX(-Math.PI / 2);
      const floor = new THREE.Mesh(floorGeo, this.floorMat);
      floor.position.set(0, 0, segL * 0.5);
      g.add(floor);

      // Side walls as glowing emissive panels (helps depth read)
      const wallGeo = new THREE.PlaneGeometry(segL, 3);
      const left = new THREE.Mesh(wallGeo, this.wallMat);
      left.rotation.y = Math.PI / 2;
      left.position.set(-CORRIDOR_HALF_WIDTH, 1.5, segL * 0.5);
      g.add(left);
      const right = new THREE.Mesh(wallGeo, this.wallMat);
      right.rotation.y = -Math.PI / 2;
      right.position.set(CORRIDOR_HALF_WIDTH, 1.5, segL * 0.5);
      g.add(right);

      // Lane stripes — thin emissive strips
      const stripeGeo = new THREE.PlaneGeometry(0.06, segL * 0.4);
      stripeGeo.rotateX(-Math.PI / 2);
      const stripeMat = new THREE.MeshBasicMaterial({ color: 0xffe680 });
      const stripeA = new THREE.Mesh(stripeGeo, stripeMat);
      stripeA.position.set(0, 0.01, segL * 0.5);
      g.add(stripeA);

      g.position.set(0, 0, i * segL);
      this.engine.scene.add(g);
      this.corridorSegments.push(g);
    }
  }

  /** Phase 2: build glowing pickup spheres. Bright green so they're
   *  unmistakable against the cooler corridor palette. */
  private buildPowerupMeshes() {
    const geo = new THREE.IcosahedronGeometry(0.45, 0);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x66ff80, emissive: 0x88ffa0, emissiveIntensity: 1.4,
      roughness: 0.25, metalness: 0.6,
    });
    for (const p of this.powerups) {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(p.x, 0.7, p.z);
      this.engine.scene.add(m);
      this.powerupMeshes.push(m);
    }
  }

  private buildObstacleMeshes() {
    // Build a pool of OBSTACLE meshes upfront (one per scheduled obstacle).
    // Each obstacle is positioned once and stays there — the camera (parent
    // of bikeGroup) moves forward in world space, so obstacles' positions
    // are fixed in the world. No recycling needed (corridor is 7200 units
    // and there's typically ~ 240 obstacles total).
    const geo = new THREE.BoxGeometry(OBSTACLE_HALF_X * 2, 2.0, OBSTACLE_HALF_Z * 2);
    const mat = new THREE.MeshStandardMaterial({
      color: 0xff4a2e, emissive: 0xff8050, emissiveIntensity: 0.7,
      roughness: 0.45, metalness: 0.5,
    });
    for (const obs of this.obstacles) {
      const m = new THREE.Mesh(geo, mat);
      m.position.set(obs.x, 1.0, obs.z);
      this.engine.scene.add(m);
      this.obstacleMeshes.push(m);
    }
  }

  // ============================================================
  // Per-frame tick
  // ============================================================

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs >= RUN_MS) { this.endRun(runMs, null); return; }
    // Boost intensity = forward speed ratio + active-multiplier kicker.
    // Drives FOV pulse + motion blur + chromatic aberration when you're
    // grazing fast through cluster runs (depth-bar post-processing).
    const speedRatio = Math.max(0, Math.min(1, this.bvz / MAX_FORWARD_SPEED));
    const multBonus = (this.multiplier - 1) / (MAX_MULTIPLIER - 1) * 0.3;
    this.engine.setBoost(Math.min(1, speedRatio + multBonus), dt);

    // v6 — adaptive audio drone tracks speed AND active multiplier so
    // the bed thickens during graze runs and the hot-streak corridor
    // moments. Per memory feedback-sibling-games-shallow §"at least
    // one escalation hook". Capped at 0.7.
    try {
      const droneTarget = 0.15 + Math.min(1, speedRatio + multBonus * 1.5) * 0.55;
      this.audio.setDroneIntensity(droneTarget);
    } catch { /* off */ }

    // Smooth lean input (continuous from digital keys).
    this.leanInput = clampLean(this.leanInput, this.leanRaw, dt, 4.0);

    // Forward auto-throttle: ramp toward MAX_FORWARD_SPEED.
    const vzTarget = MAX_FORWARD_SPEED;
    const dvz = vzTarget - this.bvz;
    this.bvz += Math.sign(dvz) * Math.min(Math.abs(dvz), FORWARD_ACCEL * dt);

    // Lateral velocity from lean, with damping pull toward zero when no input.
    if (this.leanRaw !== 0) {
      this.bvx += this.leanInput * LATERAL_ACCEL * dt;
    } else {
      this.bvx -= this.bvx * Math.min(1, LATERAL_DAMP * dt);
    }
    this.bvx = Math.max(-MAX_LATERAL_SPEED, Math.min(MAX_LATERAL_SPEED, this.bvx));

    this.bx += this.bvx * dt;
    this.bz += this.bvz * dt;

    // Clamp lateral inside corridor (and stop at the wall).
    const xLim = CORRIDOR_HALF_WIDTH - BIKE_HALF_X;
    if (this.bx > xLim) { this.bx = xLim; this.bvx = 0; }
    if (this.bx < -xLim) { this.bx = -xLim; this.bvx = 0; }

    // Advance camera rig to track bike.
    this.cameraRig.position.set(this.bx, 0, this.bz);

    // Lean visual: tilt bike around Z (forward) axis.
    this.bikeGroup.rotation.z = -this.leanInput * 0.35;

    // Recycle corridor segments past the bike.
    const segL = this.CORRIDOR_SEGMENT_LENGTH;
    for (const seg of this.corridorSegments) {
      if (seg.position.z + segL < this.bz - 4) {
        seg.position.z += segL * this.CORRIDOR_SEGMENT_COUNT;
      }
    }

    // v5: biome cycling. Lerp toward target biome palette every frame —
    // smooth blend through transitions so no abrupt color flip.
    const targetBiome = Math.floor(this.bz / BIOME_ROTATION_M) % BIOMES.length;
    this.currentBiomeIdx = targetBiome;
    this.biomesVisited.add(targetBiome);
    const palette = BIOMES[targetBiome];
    const lerpRate = Math.min(1, dt * 0.6);  // ~1.6s to settle
    this.floorMat.color.lerp(new THREE.Color(palette.floor), lerpRate);
    this.wallMat.color.lerp(new THREE.Color(palette.wall), lerpRate);
    this.wallMat.emissive.lerp(new THREE.Color(palette.wallEmissive), lerpRate);
    (this.engine.scene.background as THREE.Color).lerp(new THREE.Color(palette.bg), lerpRate);
    if (this.engine.scene.fog && (this.engine.scene.fog as THREE.Fog).color) {
      (this.engine.scene.fog as THREE.Fog).color.lerp(new THREE.Color(palette.fog), lerpRate);
    }

    // Check near-misses + collisions for newly-passed obstacles.
    while (this.nextObstacleIdx < this.obstacles.length) {
      const obs = this.obstacles[this.nextObstacleIdx];
      if (this.bz < obs.z) break;
      // Interpolate x at the moment of crossing (linear within frame).
      const prevBz = this.bz - this.bvz * dt;
      const dz = this.bz - prevBz;
      const tFrac = dz > 0 ? (obs.z - prevBz) / dz : 0;
      const prevBx = this.bx - this.bvx * dt;
      const xAtCrossing = prevBx + (this.bx - prevBx) * tFrac;
      const lateral = Math.abs(xAtCrossing - obs.x);
      const tAtCrossing = runMs - dt * 1000 * (1 - tFrac);

      if (lateral < BIKE_HALF_X + OBSTACLE_HALF_X) {
        // Collision — end run.
        this.hitEvent = { t: Math.round(tAtCrossing), obstacle_idx: obs.idx };
        this.endRun(runMs, this.hitEvent);
        return;
      }
      const edgeDist = lateral - OBSTACLE_HALF_X - BIKE_HALF_X;
      if (edgeDist <= NEAR_MISS_RADIUS) {
        const ev: NearMissEvent = {
          t: Math.round(tAtCrossing),
          obstacle_idx: obs.idx,
          lateral_distance: q01(lateral),
        };
        if (this.nearMisses.length < MAX_NEAR_MISSES) this.nearMisses.push(ev);
        this.multiplier = Math.min(MAX_MULTIPLIER, this.multiplier + 1);
        this.multiplierExpiry = runMs + MULTIPLIER_WINDOW_MS;
        if (this.multiplier > this.peakMultiplier) this.peakMultiplier = this.multiplier;
        try { this.audio.chime(this.multiplier * 80); } catch { /* audio off */ }
      }
      this.nextObstacleIdx += 1;
    }

    // Phase 2: power-up pickups. Walk in order; check crossings of
    // each upcoming pickup's z within the lateral threshold.
    while (this.nextPowerupIdx < this.powerups.length) {
      const p = this.powerups[this.nextPowerupIdx];
      if (this.bz < p.z) break;
      // Interpolate x at the crossing.
      const prevBz = this.bz - this.bvz * dt;
      const dz = this.bz - prevBz;
      const tFrac = dz > 0 ? (p.z - prevBz) / dz : 0;
      const prevBx = this.bx - this.bvx * dt;
      const xAtCrossing = prevBx + (this.bx - prevBx) * tFrac;
      if (Math.abs(xAtCrossing - p.x) <= POWERUP_PICKUP_HALF_X) {
        this.collectedPowerupIdxs.add(p.idx);
        this.powerupsCollected += 1;
        this.scoreEstimate += POWERUP_BONUS;
        // Extend the current multiplier window if any is active.
        if (this.multiplierExpiry >= 0) {
          this.multiplierExpiry = Math.max(this.multiplierExpiry, runMs + POWERUP_EXTEND_MS);
        }
        this.powerupMeshes[p.idx].visible = false;
        try { this.audio.chime(380); } catch { /* off */ }
      }
      this.nextPowerupIdx += 1;
    }

    // Expire multiplier if window passed.
    if (this.multiplierExpiry >= 0 && runMs > this.multiplierExpiry) {
      this.multiplier = 1;
      this.multiplierExpiry = -1;
    }

    // Score integration (estimate; server is authoritative).
    this.scoreEstimate += this.bvz * dt * this.multiplier;

    // Sample at SAMPLE_INTERVAL_MS cadence.
    this.sampleTimer += dt * 1000;
    if (this.sampleTimer >= SAMPLE_INTERVAL_MS) {
      this.sampleTimer = 0;
      if (this.samples.length < MAX_SAMPLES) {
        this.samples.push({
          t: Math.round(runMs),
          x: q01(this.bx),
          z: q1(this.bz),
          lean: q01(this.leanInput),
        });
      }
    }

    this.cb.onHud(runMs, this.bz, this.multiplier, Math.floor(this.scoreEstimate));
  }

  private endRun(runMs: number, hit: HitEvent | null) {
    this.gameOver = true;
    // v6 — quiet the adaptive drone so it doesn't bleed into the
    // game-over screen.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // Ensure at least one final sample at end-of-run.
    if (this.samples.length === 0 || this.samples[this.samples.length - 1].t < Math.round(runMs)) {
      if (this.samples.length < MAX_SAMPLES) {
        this.samples.push({
          t: Math.round(runMs), x: q01(this.bx), z: q1(this.bz), lean: q01(this.leanInput),
        });
      }
    }
    const result: SlipstreamResult = {
      distance_m: q1(this.bz),
      near_misses: this.nearMisses.length,
      peak_multiplier: this.peakMultiplier,
      powerups_collected: this.powerupsCollected,
      hit: hit !== null,
      duration_ms: Math.round(runMs),
      samples: this.samples,
      nearMisses: this.nearMisses,
      hitEvent: hit,
      scoreEstimate: Math.floor(this.scoreEstimate),
      biomes_visited: this.biomesVisited.size,
    };
    this.cb.onGameOver(result);
  }

  // ============================================================
  // Public lifecycle
  // ============================================================

  start() {
    this.startedAt = performance.now();
    // Seed the first sample at t=0.
    this.samples.push({ t: 0, x: 0, z: 0, lean: 0 });
    this.engine.start();
  }

  armAudio() { try { this.audio.arm(); } catch { /* gesture-gated */ } }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }

  dispose() {
    this.gameOver = true;
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the parallax starfield layers so a navigate-away →
    // remount cycle doesn't accumulate THREE.Points.
    for (const layer of this.starLayers) {
      this.cameraRig.remove(layer);
      layer.geometry.dispose();
      (layer.material as THREE.Material).dispose();
    }
    this.starLayers = [];
    this.engine.stop();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.unmountTouch();
  }
}
